import { useMemo } from 'react';

import { useGetTaskRunsQuery } from '@/services/api/blandService';
import type { TreatmentRecordRequestMetadataSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { useGetTaskUpdatesQuery } from '@/services/api/tasksService';
import type {
  AlternateContact,
  AlternateContactCollection,
} from '@/types/treatment-request-alternates';
import {
  canonicalizeHandleType,
  collectAlternatesFromRuns,
  collectAlternatesFromTaskUpdates,
  deduplicateAlternates,
  sortAlternates,
} from '@/utils/treatment-request-alternates';

export interface UseAlternateContactsResult {
  /** Combined alternates sourced from Voice AI runs, metadata, or task updates. */
  collection: AlternateContactCollection | null;
  /** Alternates sourced directly from Voice AI runs (if available). */
  voiceAiCollection: AlternateContactCollection | null;
  /** Alternates extracted from request metadata. */
  metadataCollection: AlternateContactCollection | null;
  /** Alternates parsed from task updates (fallback). */
  taskUpdateCollection: AlternateContactCollection | null;
  /** Final list of alternates for display convenience. */
  alternates: AlternateContact[];
  /** Whether either underlying query is currently loading. */
  isLoading: boolean;
  /** Whether the Voice AI runs query is loading. */
  isVoiceAiLoading: boolean;
  /** Whether the task updates query is loading. */
  isTaskUpdatesLoading: boolean;
  /** Error from Voice AI runs query, if any. */
  voiceAiError?: unknown;
  /** Error from task updates query, if any. */
  taskUpdatesError?: unknown;
}

/**
 * Fetches alternate contacts for a request, preferring Voice AI runs,
 * then request metadata, and falling back to task updates when necessary.
 */
export const useAlternateContacts = (
  matterId: string,
  taskId: string | null | undefined,
  metadata?: TreatmentRecordRequestMetadataSchema | null,
): UseAlternateContactsResult => {
  const hasTask = Boolean(taskId);

  const {
    data: runsResponse,
    isLoading: isVoiceAiLoading,
    error: voiceAiError,
  } = useGetTaskRunsQuery(
    {
      matterId,
      taskId: taskId || '',
    },
    {
      skip: !hasTask,
    },
  );

  const voiceAiCollection = useMemo<AlternateContactCollection | null>(() => {
    if (!runsResponse?.runs?.length) {
      return null;
    }

    return collectAlternatesFromRuns(runsResponse.runs);
  }, [runsResponse]);

  const {
    data: taskUpdates,
    isLoading: isTaskUpdatesLoading,
    error: taskUpdatesError,
  } = useGetTaskUpdatesQuery(
    {
      matterId,
      taskId: taskId || '',
    },
    {
      skip: !hasTask,
    },
  );

  const taskUpdateCollection =
    useMemo<AlternateContactCollection | null>(() => {
      if (!taskUpdates?.length) {
        return null;
      }

      return collectAlternatesFromTaskUpdates(taskUpdates);
    }, [taskUpdates]);

  // Extract alternates from request metadata (same source ContactDiscoveryPanel uses)
  const metadataCollection = useMemo<AlternateContactCollection | null>(() => {
    if (!metadata) return null;

    // Use structured if defined (even if empty), only fall back to legacy if null/undefined
    const structured = metadata.contact_discovery_summary?.alternate_contacts;
    const legacy = metadata.voice_ai_discovered_alternates;
    const rawAlternates = structured ?? legacy;

    if (!rawAlternates?.length) return null;

    const alternates = rawAlternates
      .filter(
        (alt): alt is typeof alt & { value: string } =>
          typeof alt.value === 'string' && alt.value.trim().length > 0,
      )
      .map((alt) => ({
        handle_type: canonicalizeHandleType(alt.handle_type || 'FAX'),
        value: alt.value.trim(),
        label: alt.label ?? null,
        confidence: typeof alt.confidence === 'number' ? alt.confidence : null,
      }));

    if (!alternates.length) return null;

    return {
      alternates: sortAlternates(deduplicateAlternates(alternates)),
      sourceId: undefined,
      sourceLabel: 'Request Metadata',
      detectedAt: metadata.contact_discovery_last_updated_at ?? undefined,
      origin: 'request_metadata' as const,
    };
  }, [metadata]);

  // Priority: runs > metadata > task updates
  const collection =
    voiceAiCollection ?? metadataCollection ?? taskUpdateCollection ?? null;

  return {
    collection,
    voiceAiCollection,
    metadataCollection,
    taskUpdateCollection,
    alternates: collection?.alternates ?? [],
    isLoading: Boolean(hasTask) && (isVoiceAiLoading || isTaskUpdatesLoading),
    isVoiceAiLoading,
    isTaskUpdatesLoading,
    voiceAiError,
    taskUpdatesError,
  };
};
